import { datadogRum, type RumInitConfiguration } from "@datadog/browser-rum";

interface User {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: unknown;
}

interface ErrorTrackingConfig {
  datadog: RumInitConfiguration;
  debug?: boolean;
}

let isInitialized = false;

function initializeErrorTracking(config: ErrorTrackingConfig): void {
  if (isInitialized) {
    if (config.debug) {
      console.warn("[ErrorTracking] Already initialized");
    }
    return;
  }

  const { datadog, debug = false } = config;

  if (!datadog.applicationId || !datadog.clientToken) {
    if (debug) {
      console.warn(
        "[ErrorTracking] Missing required Datadog configuration, skipping initialization"
      );
    }
    return;
  }

  try {
    datadogRum.init(datadog);

    if (debug) {
      console.info("[ErrorTracking] Datadog RUM initialized successfully");
    }

    isInitialized = true;
  } catch (error) {
    console.error("[ErrorTracking] Failed to initialize Datadog RUM:", error);
  }
}

function trackError(error: unknown, context?: object): void {
  if (!isInitialized) {
    console.error(
      "[ErrorTracking] Not initialized, cannot track error:",
      error
    );
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (trackingError) {
    console.error("[ErrorTracking] Failed to track error:", trackingError);
  }
}

function setUser(user: User): void {
  if (!isInitialized) {
    console.warn("[ErrorTracking] Not initialized, cannot set user");
    return;
  }

  try {
    datadogRum.setUser(user);
  } catch (error) {
    console.error("[ErrorTracking] Failed to set user:", error);
  }
}

function isErrorTrackingInitialized(): boolean {
  return isInitialized;
}

export type { RumInitConfiguration, User, ErrorTrackingConfig };
export {
  initializeErrorTracking,
  trackError,
  setUser,
  isErrorTrackingInitialized,
};
